import {
    TrainingModePageTourReplayButton,
    TrainingModePageTourScope,
} from '@apps/web/components/page_tour/training_mode_page_tour';
import { TEACH_PHOEBE_PAGE_TOUR_STEPS } from '@apps/web/components/page_tour/training_mode_tour_configs';
import { Button } from '@apps/web/components/ui/button';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { useTeachPhoebePageViewed } from '@apps/web/features/teach_phoebe/model/use_teach_phoebe_analytics';
import { NeedsReviewGroups } from '@apps/web/features/teach_phoebe/ui/needs_review_groups';
import { ProgressCard } from '@apps/web/features/teach_phoebe/ui/progress_card';
import { CoverageSection } from '@apps/web/features/training_coverage/ui/coverage_section';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useTrainingMode } from '@apps/web/hooks/use_training_mode';
import { useUser } from '@apps/web/swr_data_fetching/data_fetching_hooks';

const REASON = 'teach-phoebe-route';

export function TeachPhoebePage() {
    const organizationId = useOrganizationId();
    const { data: user } = useUser({ reason: REASON });
    const isAdmin = user?.admin === true;
    const {
        isReady,
        error: organizationError,
        retry,
        isInTrainingMode,
        trainingDay,
    } = useTrainingMode({
        organizationId,
        reason: REASON,
    });
    useTeachPhoebePageViewed({
        organizationId,
        trainingDay: isInTrainingMode ? trainingDay : null,
    });

    // Branched before the skeleton: without a cold-load organization fetch
    // failure, `isReady` never becomes true and the skeleton would render
    // forever with no way out.
    if (organizationError) {
        return (
            <div className="flex h-full items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <p className="text-sm font-medium">
                        Couldn’t load Teach Phoebe
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                        Something went wrong while loading your organization.
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={retry}
                    >
                        Try again
                    </Button>
                </div>
            </div>
        );
    }

    if (!isReady) {
        return (
            <div className="space-y-4 px-4 py-6 sm:px-6">
                <Skeleton className="h-8 w-56" />
                <Skeleton className="h-40 w-full" />
            </div>
        );
    }

    if (!isInTrainingMode || trainingDay === null) {
        return (
            <div className="flex h-full items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <h1 className="text-lg font-semibold">
                        Training mode is off
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        This page is available while Phoebe is in training for
                        your organization.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <TrainingModePageTourScope
            pageId="teach-phoebe"
            organizationId={organizationId}
            ready={isReady}
            steps={TEACH_PHOEBE_PAGE_TOUR_STEPS}
        >
            <div className="flex h-full min-w-0 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
                    <div className="mx-auto flex w-full max-w-[880px] flex-col gap-9">
                        <div className="flex flex-col gap-3.5">
                            <div className="flex items-center justify-between gap-3">
                                <h1 className="text-xl font-semibold sm:text-2xl">
                                    Teach Phoebe
                                </h1>
                                <TrainingModePageTourReplayButton />
                            </div>
                            <ProgressCard trainingDay={trainingDay} />
                        </div>
                        <section
                            className="min-w-0"
                            data-testid="teach-phoebe-needs-review"
                        >
                            <h2 className="text-muted-foreground border-b pb-2 text-xs font-semibold tracking-wider uppercase">
                                Needs review
                            </h2>
                            <NeedsReviewGroups
                                // Remount on org switch: pinned-row state is
                                // org-scoped and must not bleed across orgs.
                                key={organizationId ?? 'no-org'}
                                organizationId={organizationId}
                            />
                        </section>
                        <CoverageSection
                            organizationId={organizationId}
                            isAdmin={isAdmin}
                        />
                    </div>
                </div>
            </div>
        </TrainingModePageTourScope>
    );
}
